import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import type { UserRole } from '@familienbaum/shared';
import { BrandMark } from './brand-mark';
import { visibleGroups, type NavItem } from './nav-items';
import { Tooltip } from '@/components/ui/misc';
import { Eyebrow } from '@/components/ui/paper';
import { useArchive, useDashboard, useGapSummary, useQuestionCounts } from '@/lib/queries';
import { cn, formatNumber } from '@/lib/utils';

/**
 * Ein Ziel in der Leiste.
 *
 * Vorher stand das alles inline in `SidebarNav` – drei Ebenen Verschachtelung
 * tief, mit einem Sonderfall für Bereiche, deren Überschrift kein Ziel war.
 * Seit die Bereiche selbst Ziele sind („Stammbaum“ ist eine Seite, „Album“ war
 * nur ein Wort), braucht die Leiste genau zwei Fassungen desselben Eintrags:
 * eine für die Ebene, eine für das eingerückte Kind darunter.
 */
function NavEntry({
  item,
  collapsed,
  primary,
  child,
  badges,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  /** Hauptbereich – steht in vollem Ton da, wo die übrigen leise sind. */
  primary?: boolean;
  /** Zweiter Weg in denselben Bereich: eingerückt, eine Spur kleiner. */
  child?: boolean;
  badges: { questions: number; gaps: number; media: number };
  onNavigate?: () => void;
}) {
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          // Kein Vollrund: die Leiste ist eine getönte Fläche, und
          // der gewählte Eintrag liegt als *Kärtchen* darauf. Eine
          // Pille sähe hier wie ein Knopf aus, und geklickt wird
          // hier nichts – man ist schon da.
          'group relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm sm:py-2',
          'transition-colors duration-150',
          // Eingeklappt eine 44px-Kachel mit 14px-Rundung – 2d.
          collapsed && 'mx-auto size-11 justify-center rounded-[14px] p-0',
          // Die Hauptbereiche einen Schritt größer – 15px gegen 14px ist der
          // kleinste Unterschied, den man ohne Vergleich sieht, und mehr
          // braucht es nicht: sie stehen ohnehin oben und ohne Überschrift.
          primary && !child && !collapsed && 'text-[15px]',
          // Das Kind bleibt auf der Größe der übrigen Einträge und trägt
          // seinen Rang in der Einrückung. Kleiner gesetzt stand „Personen“
          // unter „Beziehungen“ und „Orte“ – und das ist die Rangfolge
          // verkehrt herum.
          child && !collapsed && 'py-2 sm:py-1.5',
          isActive
            ? 'bg-surface font-semibold text-ink shadow-xs'
            : cn(
                'font-medium hover:bg-surface/60 hover:text-ink',
                primary && !child ? 'text-ink' : 'text-ink-2',
              ),
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Der aktive Zustand hängt nicht an der Fläche allein:
              das Symbol wechselt nach Gold. Das ist die einzige
              Stelle in der Navigation, an der der Akzent auftaucht –
              und damit auch die einzige, die man sucht. */}
          <item.icon
            className={cn(
              'shrink-0',
              child && !collapsed ? 'size-4' : 'size-4.5',
              isActive ? 'text-accent' : 'text-ink-3',
            )}
            aria-hidden
          />
          {collapsed ? (
            <span className="sr-only">{item.label}</span>
          ) : (
            <span className="truncate">{item.label}</span>
          )}
          {/* Offene Fragen an mich – der einzige Zähler in der
              Navigation, der etwas fordert, deshalb gold und rund. */}
          {item.badge === 'questions' && badges.questions > 0 ? (
            <span
              className={cn(
                'ml-auto shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[11px] font-semibold tabular text-accent-ink',
                collapsed && 'absolute top-1 right-1 ml-0 px-1 py-0 text-[10px] leading-[14px]',
              )}
              aria-label={`${badges.questions} offene Fragen`}
            >
              {badges.questions > 99 ? '99+' : badges.questions}
            </span>
          ) : null}
          {/* Wie viel im Fotoalbum liegt – dieselbe leise Mono-Zahl wie
              bei den Lücken: sie zählt, sie fordert nichts. Eingeklappt
              entfällt sie: dort ist kein Platz für vier Ziffern, und eine
              gekappte Zahl ist schlimmer als keine. */}
          {item.badge === 'media' && badges.media > 0 && !collapsed ? (
            <span className="ml-auto shrink-0 font-mono text-[11px] text-ink-4 tabular">
              {formatNumber(badges.media)}
            </span>
          ) : null}
          {item.badge === 'gaps' && badges.gaps > 0 && !collapsed ? (
            <span
              className="ml-auto shrink-0 font-mono text-[11px] text-ink-4 tabular"
              aria-label={`${badges.gaps} offene Lücken`}
            >
              {badges.gaps > 999 ? '999+' : badges.gaps}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );

  /*
    Der Hinweis hängt an einer Hülle und nicht am Link selbst.

    `Tooltip` reicht seine Auslöser-Eigenschaften mit `asChild` an das
    Kind weiter und *verschmilzt* dabei die Klassen – als Text. Das
    `className` eines `NavLink` ist hier aber eine Funktion
    (`({ isActive }) => …`), und aus einer Funktion wird beim
    Verschmelzen ihr Quelltext: die Kachel trug am Ende 73 „Klassen“
    wie `({`, `=>` und `cn(`. Wirksam blieb davon, was zufällig ein
    gültiger Name war – `size-11` ja, `'mx-auto` mit Anführungszeichen
    davor nicht. Genau daran standen die Symbole 8px links der Mitte,
    und die Kachel des gewählten Eintrags fehlte ganz.

    Die Hülle nimmt die Verschmelzung auf sich (ihre Klasse ist
    schlicht Text), der Link behält seine Funktion.
  */
  return collapsed ? (
    <Tooltip content={item.label} side="right">
      <span className="block">{link}</span>
    </Tooltip>
  ) : (
    link
  );
}

export function SidebarNav({
  role,
  collapsed,
  onNavigate,
}: {
  role: UserRole | undefined;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const groups = visibleGroups(role);
  const counts = useQuestionCounts();
  // Was auf mich wartet: Fragen an mich, Vorschläge, die ich freigeben darf,
  // und neue Antworten auf meine eigenen Fragen. Letztere fehlten – deshalb
  // blieb es unbemerkt, wenn jemand geantwortet hatte.
  const openQuestions =
    (counts.data?.inbox ?? 0) + (counts.data?.review ?? 0) + (counts.data?.answered ?? 0);
  const openGaps = useGapSummary().data?.total ?? 0;
  /** Wie viel im Fotoalbum liegt – die Zahl neben dem Bereich. */
  const mediaCount = useDashboard().data?.stats.media ?? 0;
  const badges = { questions: openQuestions, gaps: openGaps, media: mediaCount };

  return (
    <nav aria-label="Hauptnavigation" className="flex flex-col gap-5 px-2 py-3">
      {groups.map((group, index) => (
        <div key={group.label ?? `group-${index}`} className="flex flex-col gap-0.5">
          {group.label && !collapsed ? (
            <Eyebrow className="px-3 pt-2 pb-1.5 text-[11px] tracking-[0.08em]">
              {group.label}
            </Eyebrow>
          ) : null}
          {/* Eingeklappt trägt eine Linie, was breit die Überschrift trägt.
              Nicht vor der ersten Gruppe: eine Linie ganz oben trennt nichts. */}
          {group.label && collapsed && index > 0 ? (
            <div className="mx-2 my-1 h-px bg-line" />
          ) : null}

          {group.items.map((item) => {
            const entry = (
              <NavEntry
                item={item}
                collapsed={collapsed}
                primary={group.primary}
                badges={badges}
                onNavigate={onNavigate}
              />
            );
            const children = item.children ?? [];
            if (children.length === 0) return <Fragment key={item.to}>{entry}</Fragment>;

            return (
              <Fragment key={item.to}>
                {entry}
                {/*
                  Eingeklappt gibt es keine Einrückung: dort ist die Leiste
                  eine Spalte aus 44px-Kacheln, und eine Führungslinie in
                  76px Breite wäre ein Strich neben einem Symbol. Das Kind
                  steht dann als eigene Kachel darunter – dass es zum
                  Stammbaum gehört, sagt breit die Linie und schmal der
                  Hinweistext.
                */}
                {collapsed ? (
                  children.map((child) => (
                    <NavEntry
                      key={child.to}
                      item={child}
                      collapsed
                      badges={badges}
                      onNavigate={onNavigate}
                    />
                  ))
                ) : (
                  // Die Führungslinie sitzt auf der Mitte des Zeichens
                  // darüber (12px Innenabstand + halbe 18px-Ikone), damit
                  // das Kind sichtbar an ihm hängt und nicht nur zufällig
                  // eingerückt ist.
                  <div className="ml-[1.3125rem] flex flex-col gap-0.5 border-l border-line pl-2">
                    {children.map((child) => (
                      <NavEntry
                        key={child.to}
                        item={child}
                        collapsed={false}
                        child
                        badges={badges}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/**
 * Name des Archivs, zweizeilig: oben der Name (Standard der Produktname, vom
 * Admin änderbar, von jedem für sich überschreibbar), unten die Namen der
 * Familien im Bestand. Ein Nachname allein stünde hier falsch – er würde eine
 * Linie bevorzugen und passte für die angeheiratete Seite nicht.
 */
export function SidebarBrand({ collapsed }: { collapsed: boolean }) {
  const { data: archive } = useArchive();
  const title = archive?.label?.trim() || archive?.name || 'Ahnenkiste';
  const subtitle = archive?.subtitle?.trim() || 'Familienarchiv';

  return (
    <div
      className={cn(
        // Gleich hoch wie die Kopfzeile rechts daneben (h-18), damit Logo
        // und Suchfeld auf einer Linie sitzen. Ohne Trennlinie: Marke und
        // Navigation stehen auf derselben getönten Fläche.
        'flex h-18 shrink-0 items-center gap-2.5 px-4',
        collapsed && 'justify-center px-0',
      )}
    >
      <BrandMark className="size-9" />
      {collapsed ? null : (
        <span className="min-w-0">
          <span className="block truncate font-display text-[17px] leading-5 font-medium text-ink">
            {title}
          </span>
          <span
            className="block truncate text-[11px] tracking-[0.06em] text-ink-3 uppercase"
            title={subtitle}
          >
            {subtitle}
          </span>
        </span>
      )}
    </div>
  );
}

/**
 * Der Fuß der Seitenleiste: wie groß der Bestand ist und wie viele Personen
 * schon ein Gesicht haben.
 *
 * Das ist die einzige Zahl in der Navigation, und sie steht nicht dort, um
 * Größe zu behaupten, sondern weil sie zum Weiterarbeiten einlädt: „64 % der
 * Personen haben ein Foto“ ist ein Satz, auf den man klicken will. Deshalb
 * führt er auch dorthin.
 */
export function SidebarStats({ collapsed }: { collapsed: boolean }) {
  const { data } = useDashboard();
  if (collapsed || !data) return null;

  const { persons, living } = data.stats;
  const withPhoto = Math.max(0, persons - data.gaps.withoutMedia);
  const share = persons > 0 ? Math.round((withPhoto / persons) * 100) : 0;

  return (
    <div className="border-t border-line px-4 py-3.5">
      <Eyebrow className="text-[11px] tracking-[0.08em]">Im Archiv</Eyebrow>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span className="font-display text-[26px] leading-none font-medium text-ink tabular">
          {formatNumber(persons)}
        </span>
        <span className="min-w-0 truncate text-[12.5px] text-ink-2">
          Personen · {formatNumber(living)} lebend
        </span>
      </div>

      {persons > 0 ? (
        <NavLink to="/luecken?kind=media" className="mt-2.5 block">
          <span
            className="block h-1.5 overflow-hidden rounded-full bg-line-strong"
            role="img"
            aria-label={`${share} % der Personen haben ein Foto`}
          >
            <span
              className="block h-full rounded-full bg-accent transition-[width] duration-500"
              style={{ width: `${share}%` }}
            />
          </span>
          <span className="mt-1.5 block text-[11.5px] text-ink-3">
            {share} % der Personen haben ein Foto
          </span>
        </NavLink>
      ) : null}
    </div>
  );
}
